import React from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Home, Compass, User, ChevronUp, ChevronDown } from 'lucide-react';
import { tap } from '@/lib/haptics';
import { useNavVisibility } from '@/context/NavVisibilityContext';

const items = [
  { to: '/',         label: 'Home',       icon: Home,       testid: 'tab-home' },
  { to: '/arrival',  label: 'Essentials', icon: Compass,    testid: 'tab-essentials' },
  { to: '/ai',       label: 'Trivly',     icon: null,       testid: 'tab-ai',    image: '/brand/trivly-ai-tile.png' },
  { to: '/explore',  label: 'Explore',    icon: Compass,    testid: 'tab-explore' },
  { to: '/profile',  label: 'Profile',    icon: User,       testid: 'tab-profile' },
];

export default function BottomTabBar() {
  const { pathname } = useLocation();
  const { hidden, hide, show } = useNavVisibility();

  return (
    <>
      {/* Outer wrapper does the viewport-relative centering (no transform conflict). */}
      <div
        className="fixed left-1/2 -translate-x-1/2 bottom-3 z-50 w-[calc(100vw-20px)] max-w-[420px] pointer-events-none"
        data-testid="bottom-tabbar-container"
      >
        <motion.nav
          initial={false}
          animate={{ y: hidden ? 120 : 0, opacity: hidden ? 0 : 1 }}
          transition={{ type: 'spring', stiffness: 320, damping: 32 }}
          className="w-full pointer-events-auto"
          data-testid="bottom-tabbar"
          aria-hidden={hidden}
        >
          {/* Collapse handle sitting on top of the nav */}
          <button
            type="button"
            onClick={hide}
            aria-label="Collapse navigation"
            data-testid="nav-collapse"
            className="mx-auto mb-1 flex items-center justify-center w-12 h-5 rounded-full bg-[#15291C] border border-[#15291C] text-[#A8B5AB] hover:text-[#C4FF3D] transition-colors shadow-[0_-2px_10px_rgba(15,26,19,0.18)]"
          >
            <ChevronDown className="w-3.5 h-3.5" />
          </button>
          <div className="relative mx-auto w-full rounded-[28px] bg-[#15291C] shadow-[0_-8px_36px_rgba(15,26,19,0.18)] border border-[#15291C] pb-[env(safe-area-inset-bottom)] overflow-hidden">
            <ul className="grid grid-cols-5">
              {items.map(({ to, label, icon: Icon, testid, image }) => {
                const active = to === '/' ? pathname === '/' : pathname.startsWith(to);
                return (
                  <li key={to} className="relative min-w-0">
                    <NavLink
                      to={to}
                      data-testid={testid}
                      onClick={tap}
                      className={({ isActive }) => {
                        const on = isActive || active;
                        return `flex flex-col items-center justify-center gap-0.5 py-2.5 px-1 gt-focus tap-bounce transition-colors min-w-0 ${on ? 'text-[#C4FF3D]' : 'text-[#A8B5AB] hover:text-white'}`;
                      }}
                    >
                      {image ? (
                        <span className={`w-8 h-8 rounded-lg overflow-hidden flex items-center justify-center ${active ? 'shadow-[0_0_14px_rgba(247,180,9,0.55)] ring-1 ring-[#FFB400]/60' : 'shadow-[0_4px_10px_rgba(0,0,0,0.20)]'}`}>
                          <img src={image} alt="Trivly AI" className="w-full h-full object-cover" draggable={false} />
                        </span>
                      ) : (
                        <Icon className="w-[18px] h-[18px]" strokeWidth={active ? 2.4 : 1.8} />
                      )}
                      <span className="text-[9.5px] font-bold tracking-wide leading-tight max-w-full truncate">{label}</span>
                      {active && !image && <span className="absolute bottom-1 w-1 h-1 rounded-full bg-[#C4FF3D] shadow-[0_0_8px_rgba(196,255,61,0.85)]" />}
                    </NavLink>
                  </li>
                );
              })}
            </ul>
          </div>
        </motion.nav>
      </div>

      {/* Expander arrow — wrapped same way to avoid the transform clobber */}
      <AnimatePresence>
        {hidden && (
          <div
            key="nav-expander-wrap"
            className="fixed left-1/2 -translate-x-1/2 bottom-2 z-50 pointer-events-none"
          >
            <motion.button
              type="button"
              onClick={show}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 20 }}
              transition={{ duration: 0.25 }}
              data-testid="nav-expander"
              aria-label="Show navigation"
              className="pointer-events-auto w-12 h-7 rounded-full bg-[#15291C] text-[#C4FF3D] flex items-center justify-center shadow-[0_-4px_18px_rgba(15,26,19,0.25)] border border-[#15291C]/80 hover:bg-[#1F3A29]"
            >
              <ChevronUp className="w-4 h-4" />
            </motion.button>
          </div>
        )}
      </AnimatePresence>
    </>
  );
}
